<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import {
    ArrowLeft,
    BarChart3,
    ChevronLeft,
    ChevronRight,
    Clock,
    LineChart,
    Maximize2,
    Minimize2,
    Radio,
    RefreshCw,
    Search,
  } from 'lucide-svelte';
  import { querystring, replace } from 'svelte-spa-router';
  import {
    type CandleDoc,
    type RenkoBlockDoc,
    type SessionTradeDoc,
    fetchCandles,
    fetchRenkoBlocks,
    fetchSessionTrades,
  } from '$lib/api/charts';
  import {
    type AccountKindFilter,
    type SessionSummary,
    type StatusFilter,
    fetchRenkoSessionIds,
    fetchSessionsPaginated,
  } from '$lib/api/sessions';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import { Input } from '$lib/components/ui/input';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import { daysBetween } from '$lib/utils/chart-helpers';
  import { debounce } from '$lib/utils/schedule';
  import { isSessionActiveNow } from '$lib/utils/active-window';
  import type { IChartApi } from 'lightweight-charts';
  import AppShell from './AppShell.svelte';
  import CandlestickChart from '../charts/CandlestickChart.svelte';
  import RenkoChart from '../charts/RenkoChart.svelte';
  import CopyableSessionId from './CopyableSessionId.svelte';

  // ============================================================
  // <ChartAnalysis>  -  Fase 5.
  //
  // 2 modos:
  //  1. Lista de sessions con candles + click abre el chart.
  //  2. Chart de una session: candle + renko side-by-side con sync zoom,
  //     header con symbol + customTitle + stats + Heikin Ashi toggle.
  //
  // Mejoras vs v1:
  //  - Lista filtrable con search (sessionId, symbol, account, title).
  //  - Sync zoom entre candle y renko.
  //  - Skeletons especificos (no spinner generico).
  // ============================================================

  // ----- View state -----
  let selectedSessionId = $state<string | null>(null);
  let heikinAshi = $state(false);
  // Si arrancamos en /charts?s=... significa que vinimos desde otra ruta
  // (Sessions, Live Monitor, Developer Monitor). En ese caso "Back" debe
  // volver a la ruta anterior via history. Si la session fue seleccionada
  // dentro de la lista local, "Back" vuelve a la lista (selectedSessionId=null).
  let openedFromExternalRoute = $state(false);

  // ----- List state -----
  // Server-side paginated. `pageSessions` = la pagina actual (default 25).
  // `total` = count global del set filtrado (sin page slice). Counts de
  // status/kind se piden con mini-requests (mismo patron que Sessions tab).
  let pageSessions = $state<SessionSummary[]>([]);
  let total = $state(0);
  let pages = $state(0);
  let renkoSessionIds = $state<Set<string>>(new Set());
  let listLoading = $state(true);
  let listError = $state<string | null>(null);
  let searchInput = $state('');
  let search = $state('');

  // Filtros y sort. Mapeamos sortBy local a las columnas que entiende el backend.
  type SortBy = 'recent' | 'dailyStart' | 'pnlDesc' | 'pnlAsc' | 'trades';
  let statusFilter = $state<StatusFilter>('all');
  let kindFilter = $state<AccountKindFilter>('all');
  let sortBy = $state<SortBy>('recent');

  // Paginacion
  let page = $state(1);
  let pageSize = $state(25);
  const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

  // Counts agregados (para badges de los segmented controls), mismo patron
  // que Sessions tab: mini-requests pageSize=1 sin consumir bandwidth.
  let statusCounts = $state({ all: 0, live: 0, closed: 0, active: 0 });
  let kindCounts = $state({ all: 0, live: 0, sim: 0, demo: 0, unknown: 0 });

  const debouncedSetSearch = debounce((v: string) => {
    search = v;
    page = 1; // reset a pagina 1 cuando cambia el search
  }, 250);

  // ----- Detail state (cuando hay selectedSessionId) -----
  let detailLoading = $state(false);
  let detailError = $state<string | null>(null);
  let session = $state<SessionSummary | null>(null);
  let candles = $state<CandleDoc[]>([]);
  let blocks = $state<RenkoBlockDoc[]>([]);
  let trades = $state<SessionTradeDoc[]>([]);

  // ----- URL persist -----
  function persistView(): void {
    const params = new URLSearchParams();
    if (selectedSessionId) params.set('s', selectedSessionId);
    if (heikinAshi) params.set('ha', '1');
    if (search) params.set('q', search);
    if (statusFilter !== 'all') params.set('st', statusFilter);
    if (kindFilter !== 'all') params.set('k', kindFilter);
    if (sortBy !== 'recent') params.set('srt', sortBy);
    if (page !== 1) params.set('p', String(page));
    if (pageSize !== 25) params.set('ps', String(pageSize));
    const qs = params.toString();
    replace(`/charts${qs ? `?${qs}` : ''}`);
  }

  function restoreView(qs: string): void {
    const params = new URLSearchParams(qs);
    selectedSessionId = params.get('s');
    heikinAshi = params.get('ha') === '1';
    const q = params.get('q') ?? '';
    searchInput = q;
    search = q;
    const st = params.get('st');
    if (st === 'live' || st === 'closed' || st === 'active') statusFilter = st;
    const k = params.get('k');
    if (k === 'live' || k === 'sim' || k === 'demo' || k === 'paper' || k === 'unknown') {
      kindFilter = k;
    }
    const srt = params.get('srt');
    if (
      srt === 'recent' ||
      srt === 'dailyStart' ||
      srt === 'pnlDesc' ||
      srt === 'pnlAsc' ||
      srt === 'trades'
    ) {
      sortBy = srt;
    }
    const p = Number(params.get('p'));
    if (Number.isFinite(p) && p >= 1) page = Math.floor(p);
    const ps = Number(params.get('ps'));
    if (PAGE_SIZE_OPTIONS.includes(ps as (typeof PAGE_SIZE_OPTIONS)[number])) {
      pageSize = ps;
    }
  }

  // Map del SortBy local a la columna que entiende el backend.
  // pnlDesc/Asc usan la misma columna `totalPnL` con direccion opuesta.
  function sortParams(): { sort: 'startTime' | 'totalPnL' | 'tradeCount'; dir: 'asc' | 'desc' } {
    switch (sortBy) {
      case 'pnlDesc':
        return { sort: 'totalPnL', dir: 'desc' };
      case 'pnlAsc':
        return { sort: 'totalPnL', dir: 'asc' };
      case 'trades':
        return { sort: 'tradeCount', dir: 'desc' };
      case 'dailyStart':
        // El backend no soporta sort por dailyStartTime — caemos al default
        // 'recent' (startTime desc). El cliente puede sortear dentro de la
        // pagina si lo necesita, pero por simplicidad no lo hacemos aca.
        return { sort: 'startTime', dir: 'desc' };
      default:
        return { sort: 'startTime', dir: 'desc' };
    }
  }

  // ----- List fetch -----
  async function loadList(): Promise<void> {
    listLoading = true;
    try {
      const sp = sortParams();
      const [pageRes, renkoIds] = await Promise.all([
        fetchSessionsPaginated({
          page,
          pageSize,
          status: statusFilter,
          kind: kindFilter,
          q: search || undefined,
          sort: sp.sort,
          dir: sp.dir,
        }),
        fetchRenkoSessionIds(),
      ]);
      pageSessions = pageRes.sessions;
      total = pageRes.total;
      pages = pageRes.pages;
      renkoSessionIds = new Set(renkoIds);
      listError = null;
      // Si la page actual quedo fuera de rango (e.g. cambio el filter y
      // ya no hay tantas paginas), resetear a 1 y reload.
      if (page > pageRes.pages && pageRes.pages > 0) {
        page = 1;
        await loadList();
        return;
      }
    } catch (err) {
      console.error('[charts] loadList fallo', err);
      listError = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      listLoading = false;
    }
  }

  // Counts para badges. Mini-requests pageSize=1 sin consumir bandwidth.
  // Sin status filter, asi reflejan el universo total filtrado por kind.
  async function loadCounts(): Promise<void> {
    try {
      const [allK, liveK, simK, demoK] = await Promise.all([
        fetchSessionsPaginated({ pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'live', pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'sim', pageSize: 1 }),
        fetchSessionsPaginated({ kind: 'demo', pageSize: 1 }),
      ]);
      kindCounts = {
        all: allK.total,
        live: liveK.total,
        sim: simK.total,
        demo: demoK.total,
        unknown: Math.max(0, allK.total - liveK.total - simK.total - demoK.total),
      };
      const [allS, liveS, closedS, activeS] = await Promise.all([
        fetchSessionsPaginated({ kind: kindFilter, pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'live', pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'closed', pageSize: 1 }),
        fetchSessionsPaginated({ kind: kindFilter, status: 'active', pageSize: 1 }),
      ]);
      statusCounts = {
        all: allS.total,
        live: liveS.total,
        closed: closedS.total,
        active: activeS.total,
      };
    } catch (err) {
      console.error('[charts] loadCounts fallo', err);
    }
  }

  // ----- Detail fetch -----
  async function loadDetail(sessionId: string): Promise<void> {
    detailLoading = true;
    detailError = null;
    try {
      // 1) Resolver session metadata (necesario para symbol + dates).
      const meta =
        pageSessions.find((s) => s.sessionId === sessionId) ??
        (await fetchSessionsPaginated({ pageSize: 1, q: sessionId })).sessions[0] ??
        null;
      if (!meta) {
        detailError = `Session ${sessionId} not found`;
        session = null;
        candles = [];
        blocks = [];
        trades = [];
        return;
      }
      session = meta;

      // 2) Days a query desde startTime hasta endTime (o now si live)
      const startISO =
        typeof meta.startTime === 'number'
          ? new Date(meta.startTime).toISOString()
          : (meta.startTime ?? new Date().toISOString());
      // endTime: aproximado desde performanceStats si esta closed
      const endISO = meta.isLive ? null : startISO;
      const days = daysBetween(startISO, endISO);

      // 3) Fetch en paralelo
      const [candlesRes, blocksRes, tradesRes] = await Promise.allSettled([
        fetchCandles(days, meta.symbol),
        fetchRenkoBlocks(sessionId),
        fetchSessionTrades(sessionId, 'all'),
      ]);

      candles = candlesRes.status === 'fulfilled' ? candlesRes.value : [];
      blocks = blocksRes.status === 'fulfilled' ? blocksRes.value : [];
      trades = tradesRes.status === 'fulfilled' ? tradesRes.value : [];
    } catch (err) {
      console.error('[charts] loadDetail fallo', err);
      detailError = err instanceof Error ? err.message : 'Unknown error';
    } finally {
      detailLoading = false;
    }
  }

  // ----- Handlers -----
  function openSession(sessionId: string): void {
    selectedSessionId = sessionId;
    // Apertura interna (desde la lista) — Back debe volver a la lista,
    // no a history previo.
    openedFromExternalRoute = false;
    persistView();
    void loadDetail(sessionId);
  }

  function backToList(): void {
    if (openedFromExternalRoute) {
      // Vinimos via deep-link desde otra ruta → volver al origen real.
      // history.back() preserva el state previo (filtros, scroll, etc.) de
      // Sessions/Live/Developer. Si por algun motivo no hay historia
      // (e.g. usuario abrio el link directo en una pestania nueva), fallback
      // a la lista interna.
      if (typeof window !== 'undefined' && window.history.length > 1) {
        window.history.back();
        return;
      }
    }
    selectedSessionId = null;
    session = null;
    candles = [];
    blocks = [];
    trades = [];
    persistView();
  }

  function onSearchInput(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    searchInput = v;
    debouncedSetSearch(v);
  }

  // Filtered list ya no se calcula client-side — el backend devuelve la
  // pagina filtrada + sorteada directamente. pageSessions es la fuente.
  // Sticky live se quito: el sort 'recent' (default) ya pone las live
  // arriba porque tienen startTime mas reciente. Para sorts por pnl,
  // mezclar sticky live confundia el ranking.

  // Persist URL cuando cambia cualquier filtro/sort/page o flag visual.
  $effect(() => {
    void search;
    void statusFilter;
    void kindFilter;
    void sortBy;
    void page;
    void pageSize;
    void heikinAshi;
    persistView();
  });

  // Re-fetch lista cuando cambia filtro/sort/page/pageSize/search.
  // signature evita disparos por toggles que NO afectan el query (heikinAshi).
  let lastListSig = '';
  $effect(() => {
    const sig = `${search}|${statusFilter}|${kindFilter}|${sortBy}|${page}|${pageSize}`;
    if (sig === lastListSig) return;
    lastListSig = sig;
    untrack(() => {
      void loadList();
    });
  });

  // Re-fetch counts cuando cambia el universo (search o kind).
  let lastCountsSig = '';
  $effect(() => {
    const sig = `${search}|${kindFilter}`;
    if (sig === lastCountsSig) return;
    lastCountsSig = sig;
    untrack(() => {
      void loadCounts();
    });
  });

  // Sync zoom Renko↔Candle se quito: el Renko ahora usa eje X discreto (block#)
  // y los dominios ya no coinciden con UTC seconds del candle. Cada chart se
  // maneja independiente. Mantenemos las refs por si en el futuro queremos
  // sincronizar dos candle charts entre si.
  let candleChartApi: IChartApi | null = null;
  let renkoChartApi: IChartApi | null = null;

  function onCandleChartReady(api: IChartApi): void {
    candleChartApi = api;
  }

  function onRenkoChartReady(api: IChartApi): void {
    renkoChartApi = api;
  }

  // ----- Maximize: uno por chart (in-page) + fullscreen API del browser -----
  // 'candle' | 'renko' | null. Cuando hay uno maximizado el otro se oculta
  // y el visible toma todo el ancho + altura ampliada.
  let maximized = $state<'candle' | 'renko' | null>(null);

  function toggleMaximize(which: 'candle' | 'renko'): void {
    maximized = maximized === which ? null : which;
  }

  function toggleBrowserFullscreen(el: HTMLElement | null): void {
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void el.requestFullscreen();
    }
  }

  let candleWrap: HTMLDivElement | null = $state(null);
  let renkoWrap: HTMLDivElement | null = $state(null);

  onMount(() => {
    restoreView($querystring ?? '');
    // Si al mount ya hay una session seleccionada, es porque entramos via
    // deep-link (e.g. /charts?s=20260512112305 desde Sessions). Marcamos
    // para que Back haga history.back en lugar de mostrar la lista local.
    if (selectedSessionId) openedFromExternalRoute = true;
    // loadList lo dispara el $effect de signature cuando se monta. Aca solo
    // cargamos detail si vinimos via deep-link.
    if (selectedSessionId) void loadDetail(selectedSessionId);
  });

  // ----- Formatters (espejo de SessionCard / SessionsTable) -----
  function fmtPnL(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }

  function fmtPF(pf: number | null, trades: number): string {
    if (trades === 0) return '—';
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }

  function fmtTime(t: string | number | null): string {
    if (!t) return '—';
    const d = typeof t === 'number' ? new Date(t) : new Date(t);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  function fmtAccount(name: string | null): string {
    if (!name) return '—';
    const trimmed = name.trim();
    if (/^sim\b/i.test(trimmed)) return 'SIM';
    return trimmed;
  }

  function cleanOperator(op: string): string {
    return op.replace('@rabbyt.ai', '');
  }

  // Mode badges (mismo orden y reglas que SessionsTable / SessionCard).
  interface ModeBadge {
    label: string;
    color: string;
    title: string;
  }
  function modeBadgesFor(s: SessionSummary): ModeBadge[] {
    const out: ModeBadge[] = [];
    if (s.isTimeSlotSession) {
      out.push({
        label: 'TS',
        color: 'bg-primary/20 text-primary border-primary/40',
        title: 'TimeSlots multi-config session',
      });
    }
    if (s.filterWarmupToTradingHours) {
      out.push({
        label: 'WPM',
        color: 'bg-success/20 text-success border-success/40',
        title: 'Warmup-to-trading-hours filter',
      });
    }
    if (s.dwpmActive && s.dwpmLabel) {
      out.push({
        label: `D-WPM:${s.dwpmLabel}`,
        color: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
        title: `Deterministic warmup (${s.dwpmLabel === 'Auto' ? 'auto-calculated' : `${s.dwpmLabel} sessions`})`,
      });
    }
    if (s.enableBracketReset) {
      out.push({
        label: 'BSR',
        color: 'bg-success/20 text-success border-success/40',
        title: 'Bracket Stop Reset',
      });
    }
    if (s.vNextRestored) {
      out.push({
        label: 'vNext',
        color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
        title: 'vNext warmup restored from snapshot',
      });
    }
    return out;
  }

  // Labels para los segmented controls.
  const statusLabels: Record<StatusFilter, string> = {
    all: 'All',
    live: 'Live',
    active: 'Active',
    closed: 'Closed',
  };
  const kindLabels: Record<AccountKindFilter, string> = {
    all: 'All',
    live: 'Live',
    paper: 'Paper',
    sim: 'Sim',
    demo: 'Demo',
    unknown: 'Other',
  };
  const sortLabels: Record<SortBy, string> = {
    recent: 'Most recent',
    dailyStart: 'Daily start time',
    pnlDesc: 'P&L (high → low)',
    pnlAsc: 'P&L (low → high)',
    trades: 'Trades count',
  };

  let availableKinds = $derived.by((): AccountKindFilter[] => {
    const out: AccountKindFilter[] = ['all'];
    if (kindCounts.live > 0) out.push('live');
    if (kindCounts.sim > 0) out.push('sim');
    if (kindCounts.demo > 0) out.push('demo');
    if (kindCounts.unknown > 0) out.push('unknown');
    return out;
  });
</script>

<AppShell>
  <section class="mx-auto w-full max-w-[1600px] px-4 py-8 md:px-8 md:py-10">
    {#if !selectedSessionId}
      <!-- LIST VIEW -->
      <header class="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div class="flex flex-col gap-1">
          <span class="text-eyebrow">Visualization</span>
          <h1 class="text-display text-3xl md:text-4xl">Chart Analysis</h1>
          <p class="text-muted-foreground mt-1 text-[13px]">
            Pick a session to open candle + renko charts side-by-side with synced zoom.
          </p>
        </div>
        <Button variant="outline" size="sm" onclick={() => void loadList()} disabled={listLoading}>
          <RefreshCw
            class={['size-3.5', listLoading && 'animate-spin'].filter(Boolean).join(' ')}
          />
          Refresh
        </Button>
      </header>

      <!-- Toolbar: search + status segmented + kind segmented + sort dropdown -->
      <div class="mb-4 flex flex-col gap-3">
        <div class="flex flex-col gap-3 md:flex-row md:items-center">
          <div class="relative max-w-md flex-1">
            <Search
              class="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2"
            />
            <Input
              type="search"
              placeholder="Search by sessionId, symbol, account, title..."
              class="pl-9"
              value={searchInput}
              oninput={onSearchInput}
            />
          </div>

          <!-- Sort dropdown (native select para minimo bundle; ya tenemos
               dropdowns shadcn pero un select nativo es suficiente y a11y) -->
          <label class="flex items-center gap-2 text-xs">
            <span class="text-muted-foreground uppercase tracking-wider">Sort</span>
            <select
              class="border-input bg-background text-foreground focus:ring-ring rounded-md border px-2 py-1.5 text-xs focus:outline-none focus:ring-2"
              value={sortBy}
              onchange={(e) => {
                sortBy = (e.target as HTMLSelectElement).value as SortBy;
                page = 1;
              }}
            >
              {#each Object.entries(sortLabels) as [v, lbl] (v)}
                <option value={v}>{lbl}</option>
              {/each}
            </select>
          </label>
        </div>

        <!-- Status segmented -->
        <div class="flex flex-wrap items-center gap-1">
          <span class="text-muted-foreground mr-2 text-[10px] uppercase tracking-wider">
            Status
          </span>
          {#each ['all', 'active', 'live', 'closed'] as st (st)}
            {@const sf = st as StatusFilter}
            {@const active = statusFilter === sf}
            <button
              type="button"
              class={[
                'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors',
                active
                  ? 'bg-primary/15 text-primary border-primary/40'
                  : 'text-muted-foreground hover:text-foreground hover:border-foreground/30',
              ].join(' ')}
              onclick={() => {
                statusFilter = sf;
                page = 1;
              }}
            >
              {#if sf === 'live'}
                <Radio class="size-3" />
              {/if}
              {statusLabels[sf]}
              <Badge variant="outline" class="ml-1 px-1.5 text-[10px]">
                {statusCounts[sf]}
              </Badge>
            </button>
          {/each}
        </div>

        <!-- Account kind segmented -->
        {#if availableKinds.length > 1}
          <div class="flex flex-wrap items-center gap-1">
            <span class="text-muted-foreground mr-2 text-[10px] uppercase tracking-wider">
              Account
            </span>
            {#each availableKinds as k (k)}
              {@const active = kindFilter === k}
              <button
                type="button"
                class={[
                  'inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs transition-colors',
                  active
                    ? 'bg-primary/15 text-primary border-primary/40'
                    : 'text-muted-foreground hover:text-foreground hover:border-foreground/30',
                ].join(' ')}
                onclick={() => {
                  kindFilter = k;
                  page = 1;
                }}
              >
                {kindLabels[k]}
                <Badge variant="outline" class="ml-1 px-1.5 text-[10px]">
                  {kindCounts[k as keyof typeof kindCounts]}
                </Badge>
              </button>
            {/each}
            {#if kindFilter !== 'all' && kindFilter !== 'live'}
              <span class="text-warning ml-2 text-[10px]">Showing fake/test money</span>
            {/if}
          </div>
        {/if}
      </div>

      {#if listLoading}
        <div class="space-y-2">
          {#each Array(8) as _, i (i)}
            <Skeleton class="h-28 w-full" />
          {/each}
        </div>
      {:else if listError}
        <div
          role="alert"
          class="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm"
        >
          Error loading sessions: {listError}
        </div>
      {:else if pageSessions.length === 0}
        <p class="text-muted-foreground py-12 text-center text-sm">
          No sessions match the filters.
        </p>
      {:else}
        <div class="space-y-2">
          {#each pageSessions as s (s.sessionId)}
            {@const hasRenko = renkoSessionIds.has(s.sessionId)}
            {@const badges = modeBadgesFor(s)}
            <button
              type="button"
              class={[
                'hover:bg-accent/40 flex w-full flex-col gap-2 rounded-md border p-3 text-left transition-colors',
                s.isLive && 'border-success/40 bg-success/[0.03]',
              ]
                .filter(Boolean)
                .join(' ')}
              onclick={() => openSession(s.sessionId)}
            >
              <!-- Row 1: status badge + symbol + sessionId + customTitle + daily time + chart badges -->
              <div class="flex flex-wrap items-center gap-2">
                {#if s.isLive}
                  <Badge variant="success" class="animate-live-pulse gap-1">
                    <Radio class="size-3" />
                    Live
                  </Badge>
                {:else}
                  <Badge variant="secondary">Closed</Badge>
                {/if}

                <span class="text-primary font-mono text-base font-semibold">{s.symbol}</span>
                <CopyableSessionId sessionId={s.sessionId} />

                {#if s.customTitle}
                  <span class="text-muted-foreground text-xs italic">"{s.customTitle}"</span>
                {/if}

                {#if s.dailyStartTime || s.dailyEndTime}
                  <span
                    class="border-primary/40 bg-primary/10 text-primary inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 font-mono text-xs tabular-nums"
                  >
                    <Clock class="size-3" />
                    {s.dailyStartTime ?? '?'}–{s.dailyEndTime ?? '?'}
                  </span>
                {/if}

                <div class="ml-auto flex items-center gap-1.5">
                  <Badge variant="outline" class="gap-1">
                    <LineChart class="size-3" />
                    Candles
                  </Badge>
                  {#if hasRenko}
                    <Badge variant="outline" class="border-primary/40 text-primary gap-1">
                      <BarChart3 class="size-3" />
                      Renko
                    </Badge>
                  {/if}
                </div>
              </div>

              <!-- Row 2: account + operator + start time + mode badges -->
              <div class="flex flex-wrap items-center gap-1.5 text-xs">
                <span class="text-muted-foreground">{fmtAccount(s.accountName)}</span>
                <span class="text-muted-foreground/50">·</span>
                <span class="text-muted-foreground">{cleanOperator(s.operatorId)}</span>
                <span class="text-muted-foreground/50">·</span>
                <span class="text-muted-foreground font-mono">{fmtTime(s.startTime)}</span>
                {#each badges as b (b.label)}
                  <span
                    class={[
                      'inline-flex items-center rounded border px-1.5 text-[10px] font-bold tabular-nums tracking-wider',
                      b.color,
                    ].join(' ')}
                    title={b.title}
                  >
                    {b.label}
                  </span>
                {/each}
              </div>

              <!-- Row 3: stats grid -->
              <div class="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                <div>
                  <div class="text-muted-foreground text-[10px] uppercase tracking-wider">P&L</div>
                  <div
                    class={[
                      'font-mono text-sm font-semibold tabular-nums',
                      s.totalPnL > 0 && 'text-success',
                      s.totalPnL < 0 && 'text-destructive',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {fmtPnL(s.totalPnL)}
                  </div>
                </div>
                <div>
                  <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
                    Trades
                  </div>
                  <div class="font-mono text-sm tabular-nums">{s.tradeCount}</div>
                </div>
                <div>
                  <div class="text-muted-foreground text-[10px] uppercase tracking-wider">
                    Win %
                  </div>
                  <div class="font-mono text-sm tabular-nums">
                    {s.tradeCount > 0 ? `${s.winRate.toFixed(0)}%` : '—'}
                  </div>
                </div>
                <div>
                  <div class="text-muted-foreground text-[10px] uppercase tracking-wider">PF</div>
                  <div class="font-mono text-sm tabular-nums">
                    {fmtPF(s.profitFactor, s.tradeCount)}
                  </div>
                </div>
              </div>
            </button>
          {/each}
        </div>

        <!-- Paginacion footer: Prev/Next + page indicator + pageSize selector -->
        {#if pages > 1 || total > pageSize}
          {@const from = total === 0 ? 0 : (page - 1) * pageSize + 1}
          {@const to = Math.min(total, page * pageSize)}
          <div
            class="text-muted-foreground mt-4 flex flex-wrap items-center justify-between gap-3 text-xs"
          >
            <span class="tabular-nums">
              Showing <span class="text-foreground font-medium">{from}–{to}</span>
              of <span class="text-foreground font-medium">{total}</span>
            </span>
            <div class="flex items-center gap-1.5">
              <label class="flex items-center gap-1.5">
                <span class="uppercase tracking-wider text-[10px]">Per page</span>
                <select
                  class="border-input bg-background text-foreground focus:ring-ring rounded-md border px-2 py-1 text-xs focus:outline-none focus:ring-2"
                  value={pageSize}
                  onchange={(e) => {
                    pageSize = Number((e.target as HTMLSelectElement).value);
                    page = 1;
                  }}
                >
                  {#each PAGE_SIZE_OPTIONS as opt (opt)}
                    <option value={opt}>{opt}</option>
                  {/each}
                </select>
              </label>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onclick={() => {
                  page = Math.max(1, page - 1);
                }}
              >
                <ChevronLeft class="size-3.5" />
                Prev
              </Button>
              <span class="tabular-nums px-2">
                Page <span class="text-foreground font-medium">{page}</span> of {pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pages}
                onclick={() => {
                  page = Math.min(pages, page + 1);
                }}
              >
                Next
                <ChevronRight class="size-3.5" />
              </Button>
            </div>
          </div>
        {/if}
      {/if}
    {:else}
      <!-- DETAIL VIEW -->
      <header class="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div class="flex items-center gap-3">
          <Button variant="ghost" size="sm" onclick={backToList}>
            <ArrowLeft class="size-4" />
            Back
          </Button>
          {#if session}
            <div>
              <h1 class="text-display flex items-center gap-2 text-2xl">
                <span class="font-mono text-primary">{session.symbol}</span>
                <CopyableSessionId sessionId={session.sessionId} size="md" />
              </h1>
              <p class="text-muted-foreground text-xs">
                {session.accountName ?? '—'} · {fmtTime(session.startTime)}
                {#if session.dailyStartTime && session.dailyEndTime}
                  · {session.dailyStartTime}–{session.dailyEndTime}
                {/if}
              </p>
            </div>
          {/if}
        </div>

        <div class="flex items-center gap-2">
          <Button
            variant={heikinAshi ? 'secondary' : 'ghost'}
            size="sm"
            onclick={() => {
              heikinAshi = !heikinAshi;
            }}
          >
            Heikin Ashi
          </Button>
        </div>
      </header>

      {#if detailLoading}
        <div class="space-y-3">
          <Skeleton class="h-[520px] w-full" />
          <Skeleton class="h-[520px] w-full" />
        </div>
      {:else if detailError}
        <div
          role="alert"
          class="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm"
        >
          Error loading chart: {detailError}
        </div>
      {:else if candles.length === 0 && blocks.length === 0}
        <p class="text-muted-foreground py-12 text-center text-sm">
          No candles or renko blocks available for this session.
        </p>
      {:else}
        {@const showCandle = candles.length > 0 && maximized !== 'renko'}
        {@const showRenko = blocks.length > 0 && maximized !== 'candle'}
        {@const candleHeight = maximized === 'candle' ? 780 : 520}
        {@const renkoHeight = maximized === 'renko' ? 780 : 520}
        <div
          class={[
            'grid grid-cols-1 gap-4',
            maximized === null && 'xl:grid-cols-2',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {#if showCandle}
            <div
              bind:this={candleWrap}
              class="chart-fs-host rounded-md border bg-card"
            >
              <div class="flex items-center justify-between border-b px-4 py-2">
                <h2 class="text-sm font-semibold">
                  Candles
                  <span class="text-muted-foreground ml-2 font-normal">{candles.length}</span>
                </h2>
                <div class="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={maximized === 'candle' ? 'Restore' : 'Maximize within page'}
                    onclick={() => toggleMaximize('candle')}
                  >
                    {#if maximized === 'candle'}
                      <Minimize2 class="size-4" />
                    {:else}
                      <Maximize2 class="size-4" />
                    {/if}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Fullscreen"
                    onclick={() => toggleBrowserFullscreen(candleWrap)}
                  >
                    <Maximize2 class="size-4 rotate-45" />
                  </Button>
                </div>
              </div>
              <CandlestickChart
                candles={candles}
                {trades}
                sessionStartIso={typeof session?.startTime === 'string' ? session.startTime : null}
                {heikinAshi}
                height={candleHeight}
                onReady={onCandleChartReady}
              />
            </div>
          {/if}

          {#if showRenko}
            <div
              bind:this={renkoWrap}
              class="chart-fs-host rounded-md border bg-card"
            >
              <div class="flex items-center justify-between border-b px-4 py-2">
                <h2 class="text-sm font-semibold">
                  Renko
                  <span class="text-muted-foreground ml-2 font-normal">{blocks.length} blocks</span>
                </h2>
                <div class="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={maximized === 'renko' ? 'Restore' : 'Maximize within page'}
                    onclick={() => toggleMaximize('renko')}
                  >
                    {#if maximized === 'renko'}
                      <Minimize2 class="size-4" />
                    {:else}
                      <Maximize2 class="size-4" />
                    {/if}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Fullscreen"
                    onclick={() => toggleBrowserFullscreen(renkoWrap)}
                  >
                    <Maximize2 class="size-4 rotate-45" />
                  </Button>
                </div>
              </div>
              <RenkoChart
                blocks={blocks}
                {trades}
                height={renkoHeight}
                onReady={onRenkoChartReady}
              />
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </section>
</AppShell>

<style>
  /* Cuando el wrapper entra en fullscreen del browser, queremos que:
     1. El wrapper ocupe toda la ventana (display: flex column).
     2. El header (border-b) mantenga su altura natural.
     3. El chart-host (relative w-full con height fija) crezca para ocupar
        el resto. Sobrescribimos su --chart-h a la altura disponible. */
  :global(.chart-fs-host:fullscreen) {
    display: flex;
    flex-direction: column;
    width: 100vw;
    height: 100vh;
    background: hsl(var(--card));
  }
  :global(.chart-fs-host:fullscreen .chart-host) {
    flex: 1 1 auto;
    height: auto !important;
  }
</style>
